import { Eye, FileText, ImageIcon, Share2 } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { type TransferReceiptFormat, useTransferReceipt } from "@/shared/hooks/useTransferReceipt";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

export interface TransferReceiptActionsProps {
  payoutId: string;
  /** The payout's current status: the receipt is read again when it moves. */
  status?: string | null;
  /** Signs the receipt while the organization has no identity of its own. */
  organizationName?: string | null;
  size?: "sm" | "default";
  className?: string;
  viewClassName?: string;
}

/**
 * The payout's comprobante: seen on the spot, and as an image and a PDF, shared where the phone can share
 * files and saved elsewhere.
 */
export function TransferReceiptActions({
  payoutId,
  status,
  organizationName,
  size = "default",
  className,
  viewClassName,
}: TransferReceiptActionsProps) {
  const { t } = useTranslations("transferReceipt");
  const receipt = useTransferReceipt(payoutId, { status, organizationName });
  const [viewing, setViewing] = useState(false);

  if (receipt.failed) {
    return <p className={cn("text-xs text-destructive", className)}>{t("failed")}</p>;
  }

  const button = (format: TransferReceiptFormat, buttonSize = size) => {
    const share = receipt.canShare[format];
    const ready = format === "image" ? receipt.hasImage : receipt.hasPdf;
    const label =
      format === "image"
        ? t(share ? "share_image" : "download_image")
        : t(share ? "share_pdf" : "download_pdf");
    return (
      <ColocaButton
        key={format}
        variant="default"
        size={buttonSize}
        icon={share ? Share2 : format === "image" ? ImageIcon : FileText}
        loading={receipt.loading}
        disabled={!receipt.loading && !ready}
        onClick={() => void receipt.deliver(format, share ? "share" : "download")}
      >
        {label}
      </ColocaButton>
    );
  };

  return (
    <div className={cn("flex flex-wrap gap-2", className)}>
      <ColocaButton
        variant="default"
        size={size}
        icon={Eye}
        loading={receipt.loading}
        disabled={!receipt.loading && !receipt.imageUrl}
        onClick={() => setViewing(true)}
        className={viewClassName}
      >
        {t("view")}
      </ColocaButton>
      {button("image")}
      {button("pdf")}
      <ColocaDialog
        open={viewing}
        onOpenChange={setViewing}
        title={t("title")}
        size="default"
        mobileFullscreen
        footer={
          <>
            {button("image", "default")}
            {button("pdf", "default")}
          </>
        }
      >
        {/* Sized to the dialog's own height cap, so the whole receipt shows without scrolling. */}
        {receipt.imageUrl ? (
          <img
            src={receipt.imageUrl}
            alt={t("title")}
            className="mx-auto block h-auto max-h-[calc(min(48rem,100dvh-5rem)-11rem)] w-auto max-w-full rounded-lg"
          />
        ) : (
          <ColocaSkeleton className="mx-auto aspect-[3/5] w-full max-w-xs rounded-lg" />
        )}
      </ColocaDialog>
    </div>
  );
}
